import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
} from "lucide-react";
import {
  AnnotatedTrend,
  CohortHeatmap,
  IncidentTimeline,
} from "../analytical-charts";
import { useAnalyticalContext } from "../analytical-context";
import { Confidence, PageHeader } from "../components";
import { focusFromText, signalMatchesProduct } from "../demo-scope";
import { emergingSignals, interactionInsights } from "../fixtures";
import type { EmergingSignal } from "../domain";

const states: EmergingSignal["alertState"][] = [
  "New",
  "Watching",
  "Investigating",
  "Actioned",
  "Resolved",
  "Dismissed",
];
export function SignalsPage() {
  const { filters, query } = useAnalyticalContext();
  const scopedSignals = useMemo(
    () =>
      emergingSignals.filter((signal) =>
        signalMatchesProduct(signal, filters.product),
      ),
    [filters.product],
  );
  const [selectedId, setSelectedId] = useState(emergingSignals[0]!.id);
  const [stateById, setStateById] = useState<
    Record<string, EmergingSignal["alertState"]>
  >({});
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (!scopedSignals.some((signal) => signal.id === selectedId))
      setSelectedId(scopedSignals[0]!.id);
  }, [scopedSignals, selectedId]);
  const selected =
    scopedSignals.find((signal) => signal.id === selectedId) ??
    scopedSignals[0]!;
  const href = (path: string, focus?: string) => {
    const [pathname, search = ""] = path.split("?");
    const params = new URLSearchParams(query);
    new URLSearchParams(search).forEach((value, key) => params.set(key, value));
    if (focus) params.set("focus", focus);
    const value = params.toString();
    return `${pathname}${value ? `?${value}` : ""}`;
  };
  const supporting = useMemo(
    () =>
      interactionInsights.filter((x) =>
        selected.supportingEvidenceIds.includes(x.id),
      ),
    [selected],
  );
  const contradicting = useMemo(
    () =>
      interactionInsights.filter((x) =>
        selected.contradictoryEvidenceIds.includes(x.id),
      ),
    [selected],
  );
  return (
    <>
      <PageHeader
        eyebrow="Signals · What has newly emerged or accelerated?"
        title={`${scopedSignals.length} material ${scopedSignals.length === 1 ? "signal needs" : "signals need"} proportionate attention.`}
        description="Signals rank statistically unusual observations by operational impact and evidence. An anomaly is not presented as an incident or confirmed cause."
      />
      <div className="signal-workspace">
        <section className="signal-index">
          <div className="signal-index__heading">
            <span>
              {scopedSignals.length} ranked{" "}
              {scopedSignals.length === 1 ? "signal" : "signals"}
            </span>
            <small>Impact · acceleration · confidence</small>
          </div>
          {scopedSignals.map((signal) => (
            <button
              key={signal.id}
              type="button"
              className={`signal-card ${selected.id === signal.id ? "signal-card--selected" : ""}`}
              onClick={() => setSelectedId(signal.id)}
            >
              <div className="signal-card__meta">
                <span
                  className={`severity-label severity-label--${signal.severity.toLowerCase()}`}
                >
                  {signal.severity}
                </span>
                <strong>
                  {signal.acceleration > 0 ? "+" : ""}
                  {signal.acceleration}%
                </strong>
              </div>
              <h3>{signal.title}</h3>
              <p>{signal.observation}</p>
              <div className="signal-card__footer">
                <span>{stateById[signal.id] ?? signal.alertState}</span>
                <span>{signal.confidence.value}% confidence</span>
              </div>
            </button>
          ))}
        </section>
        <section className="signal-detail">
          <div className="signal-detail__header">
            <div>
              <span
                className={`severity-label severity-label--${selected.severity.toLowerCase()}`}
              >
                {selected.severity} · {selected.trajectory}
              </span>
              <h2>{selected.title}</h2>
              <p>{selected.observation}</p>
            </div>
            <a
              className="primary-button"
              href={href("/actions", focusFromText(selected.title))}
            >
              Start investigation <ArrowRight size={15} />
            </a>
          </div>
          <div className="detail-stat-grid">
            <div>
              <span>First seen</span>
              <strong>{selected.firstSeen}</strong>
            </div>
            <div>
              <span>Affected population</span>
              <strong>{selected.affectedPopulation}</strong>
            </div>
            <div>
              <span>Operational impact</span>
              <strong>{selected.impact}</strong>
            </div>
            <div>
              <span>Evidence coverage</span>
              <strong>{selected.confidence.evidenceCoverage}%</strong>
            </div>
          </div>
          <section className="workflow-strip">
            <div>
              <span className="kicker">Alert lifecycle</span>
              <strong>{selected.owner ?? "No owner assigned"}</strong>
              <p>{selected.deliveryState.join(" · ")}</p>
            </div>
            <label>
              State
              <select
                value={stateById[selected.id] ?? selected.alertState}
                onChange={(e) =>
                  setStateById((v) => ({
                    ...v,
                    [selected.id]: e.target
                      .value as EmergingSignal["alertState"],
                  }))
                }
              >
                {states.map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </label>
            <label>
              Reason
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Required for dismissal or resolution"
              />
            </label>
          </section>
          <section className="analysis-block">
            <div className="analysis-block__title">
              <AlertTriangle size={18} />
              <div>
                <span className="kicker">Cautious explanation</span>
                <h3>Likely explanation and alternatives</h3>
              </div>
            </div>
            <div className="explanation-columns">
              <div>
                <strong>Likely explanation</strong>
                {selected.likelyCauses.map((x) => (
                  <p key={x}>
                    Observed pattern is associated with {x.toLowerCase()}.
                  </p>
                ))}
              </div>
              <div>
                <strong>Alternative explanations</strong>
                {selected.alternatives.map((x) => (
                  <p key={x}>{x}</p>
                ))}
              </div>
            </div>
            <div className="trust-row">
              <Confidence value={selected.confidence.value} />
              <span>
                <ShieldCheck size={14} /> {selected.confidence.reason}
              </span>
            </div>
          </section>
          <div className="coi-grid">
            <section className="panel analytics-panel">
              <h3>Did the observation leave its control range?</h3>
              <AnnotatedTrend signal />
            </section>
            <section className="panel analytics-panel">
              <h3>Which events align with onset?</h3>
              <IncidentTimeline />
            </section>
          </div>
          <section className="panel analytics-panel">
            <h3>Where is the signal concentrated?</h3>
            <CohortHeatmap title="Which cohorts deviate furthest from their own expected share?" />
          </section>
          <section className="analysis-block evidence-balance">
            <div className="analysis-block__title">
              <CheckCircle2 size={18} />
              <div>
                <span className="kicker">Evidence balance</span>
                <h3>Supporting and contradictory examples</h3>
              </div>
            </div>
            <div className="evidence-tabs">
              <span>{supporting.length} supporting</span>
              <span>{contradicting.length} contradictory</span>
            </div>
            {[...supporting.slice(0, 2), ...contradicting.slice(0, 1)].map(
              (item) => (
                <article key={item.id}>
                  <span
                    className={`evidence-mode evidence-mode--${item.evidenceMode}`}
                  >
                    {item.evidenceMode}
                  </span>
                  <p>{item.summary}</p>
                  <a
                    href={href(
                      `/evidence?origin=${encodeURIComponent(selected.title)}`,
                    )}
                  >
                    Verify
                  </a>
                </article>
              ),
            )}
          </section>
        </section>
      </div>
    </>
  );
}
